import {
  FIELD_PERMISSION_TYPE,
  MODULE_PERMISSION_TYPE,
  ROUTE_MODULE_PERMISSION,
} from '@/constants/portalPermissions';

export function companyHasLegacyFullAccess(entry) {
  return Array.isArray(entry?.permissions) && entry.permissions.length === 0;
}

export function companyHasModuleCode(entry, code) {
  if (!entry) {
    return false;
  }

  if (companyHasLegacyFullAccess(entry)) {
    return true;
  }

  return (entry.permissions || [])
    .filter((permission) => permission.type === MODULE_PERMISSION_TYPE)
    .some((permission) => permission.code === code);
}

export function hasAnyCompanyModulePermission(companyPermissions, code) {
  return Object.values(companyPermissions || {}).some(
    (entry) => companyHasModuleCode(entry, code),
  );
}

export function hasAnyPortalAdmin(companyPermissions) {
  return Object.values(companyPermissions || {}).some((entry) => entry.portalAdmin);
}

export function canAccessRoute(companyPermissions, routeName) {
  if (routeName === 'Contacts') {
    return hasAnyPortalAdmin(companyPermissions);
  }

  const moduleCode = ROUTE_MODULE_PERMISSION[routeName];
  if (!moduleCode) {
    return true;
  }

  return hasAnyCompanyModulePermission(companyPermissions, moduleCode);
}

export function getRouteAccessDeniedMessage(routeName) {
  if (routeName === 'Contacts') {
    return 'Você não tem permissão para acessar os contatos.';
  }

  return 'Você não tem permissão para acessar esta página.';
}

const FALLBACK_ROUTE_PRIORITY = [
  'Home',
  'Processes',
  'ProcessesReport',
  'FinanceiroPrevisaoNumerarioNovo',
  'OperacionalCalendario',
  'Analytics',
];

export function getFallbackRouteName(companyPermissions) {
  const accessibleRoute = FALLBACK_ROUTE_PRIORITY.find(
    (routeName) => canAccessRoute(companyPermissions, routeName),
  );

  return accessibleRoute || 'MyData';
}

export function filterCustomerCompanies(companies) {
  return (companies || []).filter((company) => company.customer && !company.customBroker);
}

export function getPermittedCompanyIds(companyPermissions, moduleCode) {
  if (!moduleCode) {
    return Object.keys(companyPermissions || {}).map(Number);
  }

  return Object.keys(companyPermissions || {})
    .filter((customerId) => companyHasModuleCode(companyPermissions[customerId], moduleCode))
    .map(Number);
}

export function filterCustomerCompaniesByModule(companies, companyPermissions, moduleCode) {
  const permittedIds = getPermittedCompanyIds(companyPermissions, moduleCode);

  return filterCustomerCompanies(companies)
    .filter((company) => permittedIds.includes(company.id));
}

export function filterCustomerCompaniesByPortalAdmin(companies, companyPermissions) {
  return filterCustomerCompanies(companies)
    .filter((company) => Boolean(companyPermissions[String(company.id)]?.portalAdmin));
}

export function resolveCompaniesIdsForApi(permittedCompanies, selectedOptions) {
  return resolveSelectedCompanyIds(permittedCompanies, selectedOptions).join(',');
}

export function resolveSelectedCompanyIds(permittedCompanies, selectedOptions) {
  const permittedIds = (permittedCompanies || []).map((company) => company.id);

  const selectedIds = (selectedOptions || [])
    .filter((option) => option && option.value)
    .map((option) => option.value)
    .filter((id) => permittedIds.includes(id));

  return selectedIds.length > 0 ? selectedIds : permittedIds;
}

export function getCompanyFieldPermissionCodes(entry) {
  if (!entry || companyHasLegacyFullAccess(entry)) {
    return null;
  }

  const codes = (entry.permissions || [])
    .filter((permission) => permission.type === FIELD_PERMISSION_TYPE)
    .map((permission) => permission.code);

  return new Set(codes);
}

export function resolveFieldPermissionCodes(companyPermissions, companyIds) {
  if (!companyIds || companyIds.length === 0) {
    return null;
  }

  let result = null;

  companyIds.forEach((companyId) => {
    const entry = companyPermissions[String(companyId)];
    if (!entry) {
      return;
    }

    const codes = getCompanyFieldPermissionCodes(entry);
    if (codes === null) {
      return;
    }

    if (result === null) {
      result = new Set(codes);
      return;
    }

    result = new Set([...result].filter((code) => codes.has(code)));
  });

  return result;
}
